<template>
  <div
    v-viewport-width
    class="spotlight-container"
    :class="[isPromo && 'promo-background']"
    :style="[isPromo && promotionStyleObject]"
  >
    <div class="spotlight">
      <!-- Callout -->
      <div v-if="$store.state.session.user" class="callout">
        <!-- Promotion Callout -->
        <span v-if="isPromo" v-frag>
          <img
            :src="component.image"
            alt=""
            class="img-fluid"
          >
          <div v-html="component.promo" />
        </span>
        <!-- Normal Callout -->
        <span v-else v-frag>
          <h2>
            Discover <span class="highlighted-header">essential tools</span> for your business
          </h2>
          <p>
            Explore our top deals for
            <span
              v-for="p in personas"
              :key="p.value"
              class="persona-name"
            >
              {{ p.name }}
            </span>
          </p>
        </span>

        <!-- Button -->
        <a
          v-if="component.cta_link"
          :href="component.cta_link"
          class="btn btn-primary"
          target="_blank"
        >
          {{ buttonText }}
        </a>
        <DButton v-else @click="viewDeals">
          {{ buttonText }}
        </DButton>

        <!-- Callout -->
        <div v-if="!isPromo" class="deals-callout">
          <p>
            <span class="deals-callout-header">Explore our Spotlight deals</span>
            <br>
            Recently released deals we couldn’t be more proud of
          </p>
          <!-- Flares for spotlight callout -->
          <SwoopingArrow class="swooping-arrow-icon d-none d-lg-flex" />
          <SwoopingArrowSmall class="swooping-arrow-icon d-md-flex d-lg-none" />
        </div>
      </div>

      <!-- Email capture Callout -->
      <div v-else class="callout">
        <img
          v-if="isPromo"
          :src="component.image"
          alt=""
          class="img-fluid"
        >
        <h2 v-else>
          {{ calloutHeadline }}
        </h2>
        <p>
          {{ calloutSubheadline }}
        </p>
        <form v-if="!isEmailSubmitted" @submit.prevent="$_asyncSubmitEmail">
          <label for="email">Email</label>
          <input
            v-model="emailAddress"
            type="email"
            name="email"
            aria-label="Email"
            required
            @focus="setEmailInputFocussed"
            @blur="setEmailInputFocussed(false)"
          >
          <div>
            <DButton
              :disabled="disableSubmitEmail"
              type="submit"
              @click="$_asyncSubmitEmail(false)"
            >
              Get 10% off
            </DButton>
          </div>
        </form>
        <div v-else class="success">
          <img src="~/static/success-tick.svg" alt="">
          <span>
            <b>You're in!</b>  Thanks for signing up.
          </span>
        </div>
      </div>

      <div class="deals">
        <FlickingCarousel :id="id" :autoplay="7000">
          <div
            v-for="deal in deals"
            :key="deal.id"
            class="carousel-cell"
          >
            <NuxtLink
              class="stretched-link float-left text-decoration-none"
              :to="`${deal.get_absolute_url}`"
            >
              &nbsp;
            </NuxtLink>
            <p>{{ deal.card_description }}</p>
            <h5>{{ deal.public_name }}</h5>
            <div class="price">
              <strong>${{ deal.price }}<span>/{{ getIntervalText(deal) }}</span></strong>
              <span>${{ deal.original_price }}</span>
            </div>
            <div class="image">
              <deal-impression :id="deal.id">
                <v-lazy-image
                  class="deal-image"
                  :src="`${deal?.media_url}?width=450&aspect_ratio=16:9`"
                  :src-placeholder="lazyPlaceholderImage"
                  :alt="deal.public_name"
                  height="144"
                  width="255"
                />
              </deal-impression>
            </div>
          </div>
          <div slot="prev-arrow">
            <FontAwesomeIcon icon="arrow-left" />
          </div>
          <div slot="next-arrow">
            <FontAwesomeIcon icon="arrow-right" />
          </div>
        </FlickingCarousel>
      </div>
    </div>
  </div>
</template>
<script>
import VLazyImage from 'v-lazy-image';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DButton } from '@appsumo/dorado-button';
import { PLACEHOLDER_IMG, HOME_CALLOUT_COPY_EXPERIMENT_NAME } from '@/constants/global';

import SwoopingArrow from '~/static/spotlight/swooping-arrow.svg?inline';
import SwoopingArrowSmall from '~/static/spotlight/swooping-arrow-small.svg?inline';
import DealImpression from './DealImpression.vue';
import FlickingCarousel from '../FlickingCarousel.vue';
import SubmitEmailMixin from '../../mixins/submit-email';
import { getPlanInterval } from '../../util/global';

export default {
  components: {
    FlickingCarousel,
    VLazyImage,
    FontAwesomeIcon,
    DButton,
    DealImpression,
    SwoopingArrow,
    SwoopingArrowSmall,
  },
  mixins: [SubmitEmailMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    more: {
      type: String,
      required: true,
    },
    deals: {
      type: Array,
      required: true,
    },
    component: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      lazyPlaceholderImage: PLACEHOLDER_IMG,
      emailInputFocussed: false,
    };
  },
  computed: {
    testCalloutCopy() {
      return this.$store.getters['experiments/getExperimentVariant'](
        HOME_CALLOUT_COPY_EXPERIMENT_NAME,
      ) === 'enabled';
    },
    calloutHeadline() {
      return this.testCalloutCopy
        ? 'Software deals helping 1.5M+ entrepreneurs.'
        : 'Never pay full price for software again';
    },
    calloutSubheadline() {
      return this.testCalloutCopy
        ? 'Get great software for your business at ridiculously low prices. Plus, unlock 10% off your first order when you sign up for our emails.'
        : 'Sign up to get the hottest software deals sent straight to your inbox and get 10% off your first order!';
    },
    disableSubmitEmail() {
      // let's make sure btn is active as long as user hasn't interacted w/ input
      if (!this.emailInputFocussed && !this.emailAddress) return false;

      return this.isProcessing || !this.isEmailAddressValid;
    },
    personas() {
      const personas = this.$store.state.session.user?.personas || [];
      const attr = this.$store.state.browse?.attributes?.find((a) => a.slug === 'best_for');
      let topPersonas = [this.defaultPersona];

      if (attr) {
        topPersonas = personas.map((persona) => {
          const option = attr.type_options?.options?.find((o) => o.value === persona.key);

          if (option) {
            return option;
          }

          return null;
        }).filter(Boolean).slice(0, 3);

        if (topPersonas.length < 1) {
          topPersonas = [this.defaultPersona];
        }
      }

      return topPersonas;
    },
    defaultPersona() {
      return {
        name: 'Marketing Agencies',
        value: 'marketing-agencies',
      };
    },
    isPromo() {
      return !!this.component.background;
    },
    promotionStyleObject() {
      return {
        backgroundColor: this.component.background_color,
        '--background-image': `url(${this.component.background})`,
        '--mobile-background-image': `url(${this.component.mobile_background || this.component.background})`,
      };
    },
    buttonText() {
      return this.isPromo && this.component.cta_text ? this.component.cta_text : 'View recommended deals';
    },
  },
  methods: {
    setEmailInputFocussed(emailInputFocussed = true) {
      this.emailInputFocussed = emailInputFocussed;
    },
    viewDeals() {
      // if we're on a promo spotlight, go to the collection page
      if (this.isPromo) {
        this.$router.push(this.more);
      } else {
        this.$router.push('/collections/for-you/');
      }
    },
    getIntervalText(deal) {
      const interval = getPlanInterval(deal?.unique_plan_types);

      if (interval > 0) {
        return `${interval} ${interval > 1 ? 'years' : 'year'}`;
      }

      return 'lifetime';
    },
  },
};
</script>
<style scoped lang="scss">
@import '~/styles/utils/breakpoints';
@import '~/styles/dorado/themes/dorado';

$midnight: #1b1b1b;
$gravel: #c7ced7;

.callout img {
  margin: 0 auto 10px;
}

.spotlight-container {
  max-width: var(--viewport-width, 100vw);
  background-color: $black-pearl;
  padding-top: 16px;
  padding-bottom: 40px;
  color: #fff;
  margin: -24px -20px 20px;
  overflow-x: hidden;

  &.promo-background {
    background-color: var(--background-color, $black-pearl);
    background-image: var(--mobile-background-image, none);
    background-size: cover;
  }
}

.spotlight {
  padding-left: 16px;
  padding-right: 16px;
  max-width: 2000px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  align-items: center;
}

.success {
  background: #0a840a;
  padding: 16px 20px;
  color: #fff;
  display: inline-flex;
  align-items: center;
}

.success img {
  margin-right: 16px;
}

.deals {
  width: 105%;
  overflow-x: hidden;
  margin-right: -5%;

  ::v-deep .flicking-button {
    background: $dollar;
    width: 32px;
    height: 32px;
    top: unset;
    left: unset;
    right: unset;
    bottom: -60px;
    color: $black-pearl;

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    &::before,
    &::after {
      background-color: initial;
    }

    &::before {
      display: inline-block;
      font-style: normal;
      font-variant: normal;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
    }

    &.flicking-arrow-next {
      left: 40px;
    }

    &.flicking-arrow-is-disabled {
      display: block !important;
    }
  }
}

.carousel-cell {
  position: relative;
  margin-right: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 300px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-top-color: $dollar;
  border-top-width: 4px;
  border-top-style: solid;
  background-color: $forest;
  padding: 16px;

  .stretched-link {
    width: 100%;
    height: 100%;
    display: block;
    position: absolute;
    font-size: 0;
    top: 0;
    left: 0;

    &::after {
      z-index: 2;
    }
  }

  p {
    margin: 0;
    flex-grow: 1;
    overflow: hidden;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    /* stylelint-enable property-no-vendor-prefix */
  }

  h5 {
    color: #fff;
    font-family: Barlow, sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    margin: 0;
  }

  .price {
    font-size: 16px;
    line-height: 30px;
    display: flex;
    column-gap: 16px;
    align-items: baseline;
    flex-direction: row;

    > span {
      text-decoration: line-through;
      display: block;
    }

    strong {
      font-weight: normal;
      display: block;
      font-size: 24px;
      line-height: 36px;

      span {
        font-size: 16px;
      }
    }
  }

  .image {
    img {
      border-radius: 4px;
    }
  }
}

.callout {
  display: flex;
  flex-direction: column;
  text-align: center;
  flex-shrink: 0;

  h2 {
    font-size: 26px;
    line-height: 32px;
    color: #fff;

    .highlighted-header {
      color: $dollar;
    }
  }

  a,
  > button {
    margin-top: 16px;
  }

  p {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;

    .persona-name:first-child::after {
      content: ',\00a0';
      margin-left: -2px;
    }

    .persona-name:last-child::before {
      content: ' and ';
    }

    .persona-name:first-child::before,
    .persona-name:last-child::after {
      content: '';
    }
  }

  form {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    text-align: left;

    label {
      color: #fff;
      font-size: 16px;
      line-height: 16px;
      font-family: Barlow, sans-serif;
      font-weight: 600;
    }

    input[type='email'] {
      outline: none;
      display: inline-block;
      padding: 8px 16px;
      border: 1px solid $gravel;
      background: $black-pearl;
      border-radius: 2px;
      color: #fff;
      font-size: 16px;

      &:focus {
        background: $black-pearl;
        border: 1px solid #0044c0;
      }
    }

    button {
      width: 100%;
    }

    .form-check {
      padding: 0;

      label {
        font-size: 14px;
        line-height: 21px;
        font-weight: 400;
      }
    }
  }

  .deals-callout {
    border-top: 1px solid $spruce;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 0 0;
    position: relative;
    margin-top: 24px;
    max-width: 471px;
    text-align: left;
    width: 100%;

    p {
      margin-bottom: 0;
      font-size: 14px;

      .deals-callout-header {
        font-size: 16px;
        font-weight: 700;
      }
    }

    .swooping-arrow-icon {
      display: none;
    }
  }
}

@include media-breakpoint-up(sm) {
  .spotlight {
    row-gap: 28px;
  }

  .spotlight-container {
    padding-top: 40px;
  }

  .callout {
    text-align: left;

    h2 {
      font-size: 42px;
      line-height: 52px;
    }

    p {
      font-size: 18px;
      line-height: 27px;
    }

    a {
      width: fit-content;
    }

    > button {
      max-width: 255px;
    }

    .deals-callout {
      background: $forest;
      border: 0;
      padding: 8px 16px;

      .swooping-arrow-icon {
        position: absolute;
        bottom: 0;
        right: -60px;
      }
    }
  }
}

@include media-breakpoint-up(md) {
  .spotlight-container {
    margin: -24px -30px 20px;
    padding-bottom: 60px;

    &.promo-background {
      background-image: var(--background-image, none);
    }
  }

  .deals {
    padding-bottom: 44px;
    margin-bottom: -44px;
  }
}

@include media-breakpoint-up(lg) {
  .spotlight-container {
    padding-top: 64px;
    padding-bottom: 94px;
  }

  .spotlight {
    column-gap: 80px;
    flex-direction: row;
    align-items: flex-start;
    height: 370px;
    row-gap: 40px;
  }

  .deals {
    margin-right: -16px;
  }

  .callout {
    margin-left: 100px;
    max-width: 512px;
    height: 100%;

    .deals-callout {
      background: $forest;
      margin-top: auto;

      .swooping-arrow-icon {
        position: absolute;
        right: -75px;
        bottom: 50px;
      }
    }

    form {
      max-width: 400px;

      button {
        width: auto;
      }
    }
  }

  .spotlight-container.promo-background .callout {
    margin: auto 0 auto 100px;
    height: fit-content;
  }
}
</style>
